import type { Logger } from '@datadog/browser-logs';
import { datadogLogs } from '@datadog/browser-logs';
import { env } from 'next-runtime-env';

import type { ILogger } from '@/logger/logger';
import { ConsoleLogger } from '@/logger/logger';

export class BrowserLogger implements ILogger {
  private readonly ddLogger: Logger;

  constructor(clientToken: string, environment: string) {
    const loggerName = 'ecom-order-portal';
    // Only init once per page to avoid "SDK is loaded more than once" (e.g. Next.js
    // can evaluate this module in multiple chunks or after HMR).
    if (!datadogLogs.getInitConfiguration()) {
      datadogLogs.init({
        clientToken: clientToken,
        env: environment,
        service: loggerName,
        site: 'datadoghq.com',
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
      });
    }
    this.ddLogger =
      datadogLogs.getLogger(loggerName) ?? datadogLogs.createLogger(loggerName);
  }

  public debug(message: string, options?: unknown): void {
    this.ddLogger.debug(message, options || {});
  }

  public info(message: string, options?: unknown): void {
    this.ddLogger.info(message, options || {});
  }

  public warn(message: string, options?: unknown): void {
    this.ddLogger.warn(message, options || {});
  }

  public error(message: string, options?: unknown): void {
    this.ddLogger.error(message, options || {});
  }
}

let iLogger: ILogger | undefined;

function getBrowserLogger() {
  if (iLogger) {
    return iLogger;
  }

  const clientToken = env('NEXT_PUBLIC_DD_CLIENT_TOKEN');
  const environment = env('NEXT_PUBLIC_OURA_ENV') ?? 'development';
  if (clientToken && environment !== 'development') {
    iLogger = new BrowserLogger(clientToken, environment);
  } else {
    iLogger = new ConsoleLogger();
  }
  return iLogger;
}

export const logger = getBrowserLogger();
